import React, { useState } from 'react';
import { Button } from '@flixbus/honeycomb-react';
import { Icon, IconOffer } from '@flixbus/honeycomb-icons-react';
import { VoucherFormSection } from '../../Section';
import { AllowedCountries } from '../AllowedandForbiddenCountries/AllowedCountries';
import { ForbiddenCountries } from '../AllowedandForbiddenCountries/ForbiddenCountries';
import { LinesAutocomplete } from '../Lines';
import { BrandDisallowlist, BrandAllowlist } from '../Brands';
import { TransportTypeRule } from '../TransportType';
import VoucherFormRelations from '../Relations/Relations';
import RelationPairInputs from '../Relations/RelationPairInputs';
import { Relation } from '../../../../types/Rules';
import styles from '../../voucher-form.module.scss';
import { useVoucherFormContext } from '../../Context';

interface VoucherFormCountriesAndLinesProps {
  template?: {
    allowedCountries?: boolean;
    forbiddenCountries?: boolean;
    brandDisallowlist?: boolean;
    brandAllowlist?: boolean;
    relations?: boolean;
    transportType?: boolean;
  };
}

const VoucherFormCountriesAndLines: React.FC<VoucherFormCountriesAndLinesProps> = ({
  template,
}) => {
  const { handleClearRelationRules } = useVoucherFormContext();

  const [relationType, setRelationType] = useState<Relation | ''>('');
  const [relationPairIds, setRelationPairIds] = useState<string[]>([]);

  const generateId = () => `relation-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

  const handleAddRelationPair = () => {
    setRelationPairIds(prev => [...prev, generateId()]);
  };

  const handleRelationTypeChange = (newRelationType: Relation | '') => {
    handleClearRelationRules();
    setRelationType(newRelationType);
    // Start with one relation pair
    setRelationPairIds([generateId()]);
  };

  const buttonLabels: Record<Relation, string> = {
    allowed_countries: 'Add more allowed country relations',
    forbidden_countries: 'Add more forbidden country relations',
    allowed_cities: 'Add more allowed city relations',
    forbidden_cities: 'Add more forbidden city relations',
  };

  return (
    <VoucherFormSection
      title='Countries and lines'
      subtitle='Where users are allowed to travel with the voucher.'
      inputs={
        <>
          <LinesAutocomplete label='Allowed lines' ruleType='line_whitelist' />
          <LinesAutocomplete label='Forbidden lines' ruleType='line_blacklist' />
          {template?.transportType && (
            <div className={styles.inputLarge}>
              <TransportTypeRule />
            </div>
          )}
          {template?.brandDisallowlist && <BrandDisallowlist />}
          {template?.brandAllowlist && <BrandAllowlist />}
          {template?.allowedCountries && <AllowedCountries />}
          {template?.forbiddenCountries && <ForbiddenCountries />}
          {template?.relations && (
            <VoucherFormRelations
              relationType={relationType}
              onRelationTypeChange={handleRelationTypeChange}
            />
          )}
          {template?.relations && relationType && (
            <>
              {relationPairIds.map(relationId => (
                <RelationPairInputs
                  key={relationId}
                  relationType={relationType}
                  relationId={relationId}
                />
              ))}
              <Button
                size='md'
                onClick={handleAddRelationPair}
                appearance='link'
                extraClasses={styles.addButton}
              >
                <Icon InlineIcon={IconOffer} />
                <span>{buttonLabels[relationType]}</span>
              </Button>
            </>
          )}
        </>
      }
    />
  );
};

export default VoucherFormCountriesAndLines;
